/**
 * Sprint 182 — Session Diagnostics Card.
 *
 * Mounted on /admin/security. Live snapshot of which auth channels are
 * presenting (Bearer / Cookie / Both), the active JWT's remaining lifetime,
 * and how many synthetic-auth-monitor alerts fired in the last hour. One
 * glance answers: "Is my Command Centre session healthy right now?"
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Shield, RefreshCw, CheckCircle, AlertTriangle, Clock } from 'lucide-react';
import { apiClient } from '../../api/client';

const fmtDuration = (seconds) => {
  if (seconds == null) return '—';
  if (seconds < 0) return 'EXPIRED';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

export const SessionDiagnosticsCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await apiClient.request('/api/admin/auth-debug');
      setData(r);
    } catch (e) {
      setError(e?.message || 'Could not fetch auth diagnostics');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const healthy = data && data.redundancy_optimal;
  const okOnly = data && data.redundancy_ok && !data.redundancy_optimal;
  const broken = data && !data.redundancy_ok;

  return (
    <div
      data-testid="session-diagnostics-card"
      className="bg-white rounded-2xl border-2 border-gray-200 shadow-sm p-6"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Shield size={20} style={{ color: '#F5831F' }} />
          <h3 className="text-base font-bold" style={{ color: '#1A1209' }}>
            Session Diagnostics
          </h3>
        </div>
        <button
          type="button"
          onClick={refresh}
          disabled={loading}
          className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
          style={{ color: '#666' }}
          data-testid="session-diagnostics-refresh"
        >
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-700 mb-3">
          {error}
        </div>
      )}

      {data && (
        <div className="space-y-3">
          {/* Status pill */}
          <div
            className="flex items-center gap-2 p-3 rounded-xl"
            style={{
              background: healthy ? '#E6F5E1' : okOnly ? '#FFF4E0' : '#FFE3E3',
              borderColor: healthy ? '#4CAF50' : okOnly ? '#F5831F' : '#DC2626',
              borderWidth: 1,
              borderStyle: 'solid',
            }}
            data-testid="session-diagnostics-status"
          >
            {healthy && <CheckCircle size={16} style={{ color: '#1F5C44' }} />}
            {okOnly && <AlertTriangle size={16} style={{ color: '#8C3A20' }} />}
            {broken && <AlertTriangle size={16} style={{ color: '#8C0000' }} />}
            <span
              className="text-xs font-bold uppercase tracking-wide"
              style={{
                color: healthy ? '#1F5C44' : okOnly ? '#8C3A20' : '#8C0000',
              }}
            >
              {healthy && 'Both channels healthy — Bearer + Cookie'}
              {okOnly && `Single channel only (${data.active_channels.join(', ')}) — vulnerable to wipe`}
              {broken && 'No auth channels — request would 401'}
            </span>
          </div>

          {/* Channel grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
            <div
              className="p-2.5 rounded-lg border"
              style={{
                background: data.bearer_present ? '#E6F5E1' : '#F5F5F5',
                borderColor: data.bearer_present ? '#4CAF50' : '#E0E0E0',
              }}
              data-testid="session-channel-bearer"
            >
              <p className="font-semibold uppercase tracking-wider text-[10px]" style={{ color: '#666' }}>
                Bearer header
              </p>
              <p className="font-bold mt-0.5" style={{ color: data.bearer_present ? '#1F5C44' : '#6B6257' }}>
                {data.bearer_present ? 'Present' : 'Missing'}
              </p>
            </div>
            <div
              className="p-2.5 rounded-lg border"
              style={{
                background: data.cookie_present ? '#E6F5E1' : '#F5F5F5',
                borderColor: data.cookie_present ? '#4CAF50' : '#E0E0E0',
              }}
              data-testid="session-channel-cookie"
            >
              <p className="font-semibold uppercase tracking-wider text-[10px]" style={{ color: '#666' }}>
                hibff_auth cookie
              </p>
              <p className="font-bold mt-0.5" style={{ color: data.cookie_present ? '#1F5C44' : '#6B6257' }}>
                {data.cookie_present ? 'Present' : 'Missing'}
              </p>
            </div>
          </div>

          {/* JWT expiry */}
          <div className="flex items-center gap-2 p-2.5 rounded-lg bg-gray-50 border border-gray-200" data-testid="session-jwt-exp">
            <Clock size={14} style={{ color: '#666' }} />
            <div className="text-xs">
              <span className="font-semibold" style={{ color: '#666' }}>JWT expires in:</span>{' '}
              <span className="font-bold" style={{ color: '#1A1209' }}>
                {fmtDuration(data.token_seconds_remaining)}
              </span>
              {data.token_exp && (
                <span className="ml-2 text-[11px]" style={{ color: '#6B6257' }}>
                  ({new Date(data.token_exp).toLocaleString()})
                </span>
              )}
            </div>
          </div>

          {/* Auth monitor alerts */}
          {data.auth_monitor_alerts_last_60min > 0 && (
            <div
              className="flex items-center gap-2 p-2.5 rounded-lg bg-orange-50 border border-orange-200 text-xs"
              data-testid="session-monitor-alerts"
            >
              <AlertTriangle size={14} style={{ color: '#F5831F' }} />
              <span style={{ color: '#8C3A20' }}>
                Synthetic auth monitor fired{' '}
                <strong>{data.auth_monitor_alerts_last_60min}</strong> alert(s) in the last hour.
              </span>
            </div>
          )}

          {/* Foot */}
          <p className="text-[11px] text-center mt-2" style={{ color: '#6B6257' }}>
            Signed in as <code>{data.user_email}</code> · Server time: {new Date(data.server_time).toLocaleTimeString()}
          </p>
        </div>
      )}
    </div>
  );
};

export default SessionDiagnosticsCard;
